import React, { useContext, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import { IconButton, InputAdornment, Link } from "@mui/material";
import VisibilityIcon from "../../../../assets/view_eyeIcon.svg";

import './CreateUsers.css';

// APIs & Authorization
import { fetchUserOrganisations, getOrgRoles } from '../../../../apis/organisations.api';
import { createUser, getUserRoles, fetchUserOrgProjects } from '../../../../apis/users.api';
import { AuthContext } from '../../../../AuthContext';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';

// Custom Popper component
const CustomPopper = styled(Popper)(({ theme }) => ({
  width: 'auto !important',
  minWidth: '45%',
  zIndex: theme.zIndex.modal,
}));

function CreateUsers({ 
    openCreateUsers, 
    setOpenCreateUsers, 
    setIsToastOpen, 
    setToastMessage, 
    setLoading,
    setErrorCode
 }) {

    const {authData} = useContext(AuthContext);
    const {access_token, token_type} = authData;
    const [showPassword, setShowPassword] = useState(false);
    const [orgId, setOrgId] = useState({"org_id_list": [0], "roles": [0]});
    const queryClient = useQueryClient();
    const {data: orgsData, isLoading: loadingOrgs} = useQuery({
        queryKey: ['organisation', {access_token, token_type}], 
        queryFn: fetchUserOrganisations
    })
    const {data: projectsData, isLoading: loadingProjects} = useQuery({
        queryKey: ['projects', {orgId, access_token, token_type}], 
        queryFn: fetchUserOrgProjects, 
        enabled: !!orgsData
    })
    const { data: getUserOrgRoles, isLoading: loadingUserOrgRoles } = useQuery({
        queryKey: ['userOrgRoles', {role_type:"org_role", access_token, token_type}],
        queryFn: getUserRoles,
        enabled: !!access_token || !!orgsData
    });
    const { data: getUserProjRoles, isLoading: loadingUserProjRoles } = useQuery({
        queryKey: ['userProjRoles', {role_type:"project_role", access_token, token_type}],
        queryFn: getUserRoles,
        enabled: !!access_token || !!projectsData
    });

    const [formValues, setFormValues] = useState({
        name: '',
        email: '',
        password: '',
        organisations: [{ org_id: null, role_id: null}],
        projects: [{project_id: null, role_id: null}]
    })

    useEffect(() => {
        const orgIds = formValues.organisations
            .map(org => org?.org_id)
            .filter(orgId => orgId !== null);
        const roleIds = formValues?.organisations
            ?.map(org => org?.role_id)
            ?.filter(roleId => roleId !== null);
        setOrgId({ org_id_list: orgIds, roles: roleIds });
    }, [formValues?.organisations])

    const handleAddNewUser = () => {
        setFormValues((prevValues) => ({
            ...prevValues,
            organisations: [...prevValues.organisations, { org_id: null, role_id: null }]
        }));
    };

    const handleAddNewUserProject = () => {
        setFormValues((prevValues) => ({
            ...prevValues,
            projects: [...prevValues.projects, { project_id: null, role_id: null }]
        }));
    };

    const handleChange = (index, field, value) => {
        const newOrganisations = [...formValues.organisations];
        newOrganisations[index][field] = value;
        setFormValues((prevValues) => ({
            ...prevValues,
            organisations: newOrganisations
        }));
    };

    const handleChangeProject = (index, field, value) => {
        const newProjects = [...formValues.projects];
        newProjects[index][field] = value;
        setFormValues((prevValues) => ({
            ...prevValues,
            projects: newProjects
        }));
    };

    const handleRemoveUser = (index) => {
        const newOrganisations = formValues.organisations.filter((_, i) => i !== index);
        setFormValues((prevValues) => ({
            ...prevValues,
            organisations: (newOrganisations?.length) > 0 ? newOrganisations : [{ org_id: null, role_id: null }]
        }));
    };

    const handleRemoveUserProject = (index) => {
        const newProjects = formValues.projects.filter((_, i) => i !== index);
        setFormValues((prevValues) => ({
            ...prevValues,
            projects: (newProjects?.length) > 0 ? newProjects : [{ project_id: null, role_id: null }]
        }));
    };

    const mutation = useMutation({
        mutationFn: createUser,
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            setToastMessage("User has been created successfully");
            setErrorCode(res?.status);
            setLoading(false);
            setIsToastOpen(true);
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
            setToastMessage(`Error: ${errorMessage}`);
            setErrorCode(error?.response?.status);
            setLoading(false);
            setIsToastOpen(true);
        }
    })

    const createUserFormSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        setOpenCreateUsers(false);
        const payload = {
            name: formValues?.name,
            email: formValues?.email,
            password: formValues?.password,
            organisations: formValues?.organisations?.map(org=>({org_id: org.org_id, role_id: org.role_id})),
            projects: formValues.projects
                .filter(project => project.project_id !== null && project.role_id !== null)
                .map(project => ({ project_id: project.project_id, role_id: project.role_id }))
            
        }

        mutation.mutate({user: payload, access_token, token_type})
    }

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <>
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={openCreateUsers}
                onClose={() => setOpenCreateUsers(false)}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
                className="createUserModal"
            >
                <Fade in={openCreateUsers}>
                    <Box className="createUserModalClass">
                        <div className="createUserModalHeader">
                            <Typography
                                id="transition-modal-title"
                                variant="h6"
                                component="h2"
                                className="createUserModalTitle"
                            >
                                Create User
                            </Typography>
                            <span
                                className="createUserModalClose"
                                onClick={() => setOpenCreateUsers(false)}
                            >
                                &times;
                            </span>
                        </div>
                        <form className="g-3" onSubmit={(e)=>createUserFormSubmit(e)}>
                            <div className="createUserModalBody">
                                <div className='superAdminTitle'>User Information</div>
                                <div className="d-flex" style={{ margin: '16px 0', position: 'relative' }}>
                                    <div className="col-6" style={{ paddingRight: '12px' }}>
                                        <label htmlFor="org-name" className="form-label">
                                            User Name<span style={{color: 'red'}}>&#65290;</span>
                                        </label>
                                        <TextField
                                            required
                                            id="org-name"
                                            placeholder="User name"
                                            sx={{ width: "100%" }}
                                            value={formValues.name}
                                            onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-6" style={{ paddingRight: '12px' }}>
                                        <label htmlFor="email-id" className="form-label">
                                            Email ID<span style={{color: 'red'}}>&#65290;</span>
                                        </label>
                                        <TextField
                                            required
                                            id="email-id"
                                            placeholder="Email ID"
                                            sx={{ width: "100%" }}
                                            value={formValues.email}
                                            onChange={(e) => setFormValues({ ...formValues, email: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="d-flex" style={{ margin: '16px 0', position: 'relative' }}>
                                    <div className="col-6" style={{ paddingRight: '12px' }}>
                                        <label htmlFor="pswd" className="form-label">
                                            Password<span style={{color: 'red'}}>&#65290;</span>
                                        </label>
                                        <TextField
                                            required
                                            id="pswd"
                                            placeholder="Password"
                                            type={showPassword ? "text" : "password"}
                                            sx={{ width: "100%" }}
                                            value={formValues.password}
                                            onChange={(e) => setFormValues({ ...formValues, password: e.target.value })}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton edge="end" style={{marginRight:'0'}} onClick={togglePasswordVisibility}>
                                                            <img src={VisibilityIcon} alt="EditIcon"/>
                                                        </IconButton>
                                                    </InputAdornment >
                                                ),
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className='superAdminTitle'>Organizations</div>
                                <div style={{ margin: '16px 0' }}>
                                    {formValues.organisations.map((org, index) => (
                                        <div key={index} style={{ marginTop: '16px' }}>
                                            <Link
                                                sx={{
                                                    display: 'flex',
                                                    textDecoration: 'none',
                                                    justifyContent: 'flex-end',
                                                    marginRight: '12px',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => handleRemoveUser(index)}
                                            >
                                                Remove
                                            </Link>
                                            <div className="d-flex">
                                                <div className="col-6" style={{ paddingRight: '12px' }}>
                                                    <label htmlFor="username" className="form-label">Organization Name{index === 0 && <span style={{color: 'red'}}>&#65290;</span>}</label>
                                                    <Autocomplete
                                                        disablePortal
                                                        options={orgsData?.filter(org => !org?.is_deleted) || []}
                                                        getOptionLabel={(option) => option.name}
                                                        value={(orgsData?.filter(org => !org?.is_deleted))?.find(o => o?.org_id === org?.org_id) || null}
                                                        onChange={(event, newValue) => {
                                                            handleChange(index, 'org_id', newValue ? (newValue?.org_id) : null);
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                required
                                                                placeholder="Enter Organization Name"
                                                                name="name"
                                                                sx={{ width: "100%" }}
                                                            />
                                                        )}
                                                        PopperComponent={(props) => <CustomPopper {...props} placement='bottom-start'/>}
                                                    />
                                                </div>
                                                <div className="col-6" style={{ paddingRight: '12px' }}>
                                                        <label htmlFor="role" className="form-label">Role{index === 0 && <span style={{color: 'red'}}>&#65290;</span>}</label>
                                                    <Autocomplete
                                                        disablePortal
                                                        options={getUserOrgRoles || []}
                                                        getOptionLabel={(option) => option?.role_name}
                                                        value={getUserOrgRoles?.find(r => r?.role_id === org?.role_id) || null}
                                                        onChange={(event, newValue) => {
                                                            handleChange(index, 'role_id', newValue ? (newValue?.role_id) : null);
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                required
                                                                placeholder="Enter Role"
                                                                name="role"
                                                                sx={{ width: "100%" }}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={handleAddNewUser}
                                        sx={{
                                            textTransform: 'capitalize',
                                            fontFamily: 'open sans',
                                            fontWeight: 400,
                                            borderRadius: '0',
                                            m: '16px 0'
                                        }}
                                    >
                                        Add more
                                    </Button>
                                </div>
                                <div className='superAdminTitle'>Projects</div>
                                <div style={{ margin: '16px 0' }}>
                                    {formValues.projects.map((project, index) => (
                                        <div key={index} style={{ marginTop: '16px' }}>
                                            <Link
                                                sx={{
                                                    display: 'flex',
                                                    textDecoration: 'none',
                                                    justifyContent: 'flex-end',
                                                    marginRight: '12px',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => handleRemoveUserProject(index)}
                                            >
                                                Remove
                                            </Link>
                                            <div className="d-flex">
                                                <div className="col-6" style={{ paddingRight: '12px' }}>
                                                    <label htmlFor="project-name" className="form-label">Project Name</label>
                                                    <Autocomplete
                                                        disablePortal
                                                        options={projectsData?.filter(proj => !proj?.is_deleted) || []}
                                                        getOptionLabel={(option) => option.name}
                                                        value={(projectsData?.filter(proj => !proj?.is_deleted))?.find(p => p?.project_id === project?.project_id) || null}
                                                        onChange={(event, newValue) => {
                                                            handleChangeProject(index, 'project_id', newValue ? (newValue?.project_id) : null);
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                placeholder="Enter Project Name"
                                                                name="name"
                                                                sx={{ width: "100%" }}
                                                            />
                                                        )}
                                                        PopperComponent={(props) => <CustomPopper {...props} placement='bottom-start'/>}
                                                    />
                                                </div>
                                                <div className="col-6" style={{ paddingRight: '12px' }}>
                                                    <label htmlFor="role" className="form-label">Role</label>
                                                    <Autocomplete
                                                        disablePortal
                                                        options={(projectsData?.length > 0) ? (projectsData[index]?.role): []}
                                                        getOptionLabel={(option) => option?.role_name}
                                                        value={(projectsData?.length> 0 ) ? (projectsData[index]?.role)?.find(r => r?.role_id === project?.role_id) : null}
                                                        onChange={(event, newValue) => {
                                                            handleChangeProject(index, 'role_id', newValue ? (newValue?.role_id) : null);
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                placeholder="Enter Role"
                                                                name="role"
                                                                sx={{ width: "100%" }}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={handleAddNewUserProject}
                                        sx={{
                                            textTransform: 'capitalize',
                                            fontFamily: 'open sans',
                                            fontWeight: 400,
                                            borderRadius: '0',
                                            m: '16px 0'
                                        }}
                                    >
                                        Add more
                                    </Button>
                                </div>
                            </div>
                            <div className="createUserFormButtons">
                                <Button variant="outlined" size="large" onClick={() => setOpenCreateUsers(false)}>
                                    Cancel
                                </Button>
                                <Button variant="contained" size="large" type="submit">Create</Button>
                            </div>
                        </form>
                    </Box>
                </Fade>
            </Modal>
        </>
    );
}

export default CreateUsers;